/**
 * FASE2-BUSCADOR-3b — Lectura de los campos del mandato para su tarjeta.
 *
 * Vive aparte del componente para poder probarlo sin renderizar, y aparte de
 * ListingFieldsGrid porque ese lo comparten la tarjeta del anuncio de venta,
 * TransactionPage y CheckoutPage: tocarlo para el mandato habría afectado a
 * tres superficies que no tienen nada que ver con esto.
 *
 * Los rangos se leen con formatScaleRange, el MISMO formateador que usan el
 * wizard al publicar y la ficha del anuncio, para que las tres superficies no
 * puedan decir cosas distintas del mismo dato.
 */

import { formatScaleRange } from '../../util/scaleRange';
import { MANDATO_RANGE_GROUPS, rangeGroupById } from '../../util/mandatoRanges';
import { localizeEnumOptions } from '../../util/listingFieldLabels';

// Cuántos caracteres del final del UUID forman la referencia visible.
const REF_LENGTH = 6;

// Card-only copy: the wizard and listing detail keep the full operation labels.
const SHORT_OPERATION_LABELS = {
  'venta-total': 'MandatoListingCard.operation.purchase',
  'inversor-minoritario': 'MandatoListingCard.operation.minority',
  'inversor-mayoritario': 'MandatoListingCard.operation.majority',
};

/**
 * Referencia corta del anuncio. Dos compradores del mismo sector y zona
 * generan títulos idénticos, así que la tarjeta necesita algo que los
 * distinga.
 *
 * Se usa el FINAL del UUID, no el principio: los identificadores de Sharetribe
 * van ordenados por tiempo, así que los primeros caracteres colisionan entre
 * anuncios creados el mismo día. Medido sobre 77 anuncios, los 6 primeros
 * daban 39 valores distintos y los 6 últimos, 77.
 *
 * @param {string} uuid identificador del listing
 * @returns {string|null} referencia en mayúsculas, o null si no hay id
 */
export const refCorta = uuid =>
  uuid
    ? String(uuid)
        .replace(/-/g, '')
        .slice(-REF_LENGTH)
        .toUpperCase()
    : null;

/**
 * Texto de un conjunto (multi-enum) para una celda de la tarjeta.
 *
 * Una celda ocupa un tercio de la tarjeta: enumerar dos o tres etiquetas
 * ("Compra del negocio completo, Entrar como inversor minoritario") la
 * desborda. En vez de truncar con puntos suspensivos —que corta sin decir
 * cuánto falta— se muestra la primera y cuántas quedan. La ficha completa
 * está a un clic.
 *
 * @param {string} fieldKey clave del listing field, para sus etiquetas propias
 * @param {Array} enumOptions opciones del field config
 * @param {Array<string>} selected valores guardados
 * @param {Object} intl
 * @returns {string} texto listo para la celda
 */
export const formatSetCell = (fieldKey, enumOptions = [], selected, intl) => {
  const marcados = Array.isArray(selected) ? selected : [];
  if (marcados.length === 0) {
    return '—';
  }
  // Todas marcadas es exactamente lo que significa "me vale cualquiera".
  if (marcados.length >= enumOptions.length && enumOptions.length > 0) {
    return intl.formatMessage({ id: 'ListingPage.mandato.cualquiera' });
  }
  const opciones = localizeEnumOptions(fieldKey, enumOptions, intl);
  const etiquetas = marcados
    .map(v => {
      const option = opciones.find(o => `${o.option}` === `${v}`);
      const shortLabel = fieldKey === 'tiposOperacion' && SHORT_OPERATION_LABELS[v];
      return option && shortLabel ? intl.formatMessage({ id: shortLabel }) : option?.label;
    })
    .filter(Boolean);
  if (etiquetas.length === 0) {
    return '—';
  }
  const restantes = etiquetas.length - 1;
  return restantes > 0
    ? intl.formatMessage(
        { id: 'MandatoListingCard.masValores' },
        { primero: etiquetas[0], n: restantes }
      )
    : etiquetas[0];
};

/**
 * Texto de una de las cuatro dimensiones de intervalo.
 * @returns {string} "Cualquiera", "Más de X", "Hasta Y" o "De X a Y"
 */
export const formatRangeCell = (groupId, publicData = {}, intl) => {
  const group = rangeGroupById(groupId);
  if (!group) {
    return '—';
  }
  const min = publicData[group.minKey];
  const max = publicData[group.maxKey];
  // Un mandato anterior a esta dimensión no tiene los extremos guardados.
  if (min == null || max == null) {
    return '—';
  }
  return formatScaleRange(group, min, max, intl) || '—';
};

export { MANDATO_RANGE_GROUPS };
